import React, { createContext, useContext, useEffect, useState } from "react";
import { useIsApp } from "hooks/utils/useIsApp";
import { useMorningAuthStore } from "store/useMorningAuthStore";

const AppContext = createContext({ isApp: false, tokenReady: false, appVersion: "" });

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
    const { isApp, appVersion } = useIsApp();
    const [tokenReady, setTokenReady] = useState(false);
    // const alertShown = useRef(false);

    useEffect(() => {
        const existingToken = useMorningAuthStore.getState().accessToken;

        console.log(`[AUTH-WEB-1] AppContext useEffect 실행: isApp=${isApp}, existingToken=${existingToken ? existingToken.substring(0, 20) + "..." : "null"}`);

        // ✅ 웹뷰 접근이 아니거나 이미 토큰이 있으면 리턴
        if (!isApp || existingToken) {
            console.log(`[AUTH-WEB-2] 즉시 tokenReady=true (isApp=${isApp}, hasToken=${!!existingToken})`);
            setTokenReady(true);
            return;
        }

        // ✅ Flutter에서 설정한 전역 변수 확인 (pull-based)
        const appTokens = (window as any).__APP_TOKENS__;
        console.log(`[AUTH-WEB-3] window.__APP_TOKENS__ 확인: ${appTokens ? "존재, accessToken=" + (appTokens.accessToken ? appTokens.accessToken.substring(0, 20) + "..." : "null") : "없음"}`);

        if (appTokens?.accessToken) {
            console.log("[AUTH-WEB-4] __APP_TOKENS__에서 토큰 발견 → setAuth 호출");
            useMorningAuthStore.getState().setAuth({
                accessToken: appTokens.accessToken,
                ...appTokens.data,
            });
            setTokenReady(true);
            delete (window as any).__APP_TOKENS__;
            console.log("[AUTH-WEB-4] tokenReady=true, __APP_TOKENS__ 삭제 완료");
            return;
        }

        // 앱으로 부터 받은 메세지 처리 (fallback)
        console.log("[AUTH-WEB-5] postMessage 리스너 등록 (fallback)");
        const handleMessage = (event: MessageEvent) => {
            let payload: any;

            if (typeof event.data === "string") {
                try {
                    payload = JSON.parse(event.data);
                } catch {
                    return;
                }
            } else if (typeof event.data === "object" && event.data !== null) {
                payload = event.data;
            } else {
                return;
            }

            if (payload?.type === "SET_TOKENS") {
                const outerToken = payload.data?.accessToken;
                const inner = payload.data?.data;

                console.log(`[AUTH-WEB-6] SET_TOKENS 메시지 수신: accessToken=${outerToken ? outerToken.substring(0, 20) + "..." : "null"}`);

                if (outerToken) {
                    useMorningAuthStore.getState().setAuth({
                        accessToken: outerToken,
                        ...inner,
                    });
                    console.log("[AUTH-WEB-7] setAuth 완료 → tokenReady=true");
                } else {
                    console.log("[AUTH-WEB-ERROR] SET_TOKENS 수신했지만 accessToken이 없음");
                }

                setTokenReady(true);
            }
        };

        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [isApp]);

    // 테스트용 임시 코드
    // const alertShown = useRef(false);
    // useEffect(() => {
    //     const { host } = window.location;
    //     const parts = host.split(".");
    //     const isDomainDevelop = parts.includes("likeweb"); // 개발운영모드

    //     if (isDomainDevelop && isApp && tokenReady && accessToken && !alertShown.current) {
    //         alertShown.current = true;
    //         alert("현재 저장된 토큰: " + accessToken);
    //     }
    // }, [isApp, tokenReady, accessToken]);

    return <AppContext.Provider value={{ isApp, tokenReady, appVersion }}>{children}</AppContext.Provider>;
};

export const useAppContext = () => useContext(AppContext);
